/**
 * El recuadro «Gastos» de Inicio (lo pidió el dueño, 2026-09-29).
 *
 * Clientes decían que los gastos de caja solo se veían en el historial de la
 * caja. Aquí están a la vista al entrar: lo gastado hoy, lo del mes y los
 * últimos, cada uno con la caja y el turno de donde salió.
 *
 * ⚠️ Lee lo mismo que la pantalla de Gastos (`caja.gastos`, clave
 * `["gastos", …]`): la cifra de aquí y la de allá no pueden diferir, y
 * registrar un gasto pone al día las dos.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Tarjeta, TituloSeccion } from "@/components/ui";
import { rangoDelAtajo } from "@/components/FiltroDeFechas";
import { caja } from "@/lib/endpoints";
import { fechaLocal } from "@/lib/fechas";
import { formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";
import { plural } from "@/lib/texto";
import { esGasto, FilaDeEgreso } from "@/features/caja/egresos";

export function GastosDeInicio({ formato }: { formato: FormatoMoneda }) {
  const mes = rangoDelAtajo("mes");
  const { data } = useQuery({
    queryKey: ["gastos", mes.desde, mes.hasta],
    queryFn: () => caja.gastos(mes),
  });

  if (!data) return null;

  // Llegan del más reciente al más antiguo.
  const gastos = data.movimientos.filter(esGasto);
  const hoy = fechaLocal();
  const deHoy = gastos.filter((e) => fechaLocal(new Date(e.fecha)) === hoy);
  const totalHoy = deHoy.reduce((s, e) => s + toNumber(e.monto_en_principal), 0);
  const ultimos = gastos.slice(0, 4);

  return (
    <Tarjeta padding={false}>
      <div className="px-4 pt-4">
        <TituloSeccion
          titulo="Gastos"
          descripcion="Los de caja y los registrados en Gastos, con la caja de donde salieron."
          accion={
            <Link
              to="/gastos"
              className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              Ver gastos <ChevronRight size={14} />
            </Link>
          }
        />
      </div>

      {/* Las cifras a la izquierda y los últimos a la derecha; en el celular,
          uno debajo del otro. */}
      <div className="grid border-t border-line lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)]">
        <div className="grid grid-cols-2 gap-4 p-4 lg:grid-cols-1 lg:border-r lg:border-line">
          <div>
            <p className="rotulo">Hoy</p>
            <p
              className={`tabular mt-1 text-2xl font-semibold ${totalHoy > 0 ? "text-danger" : "text-ink"}`}
            >
              {formatMoney(totalHoy.toFixed(2), formato)}
            </p>
            <p className="mt-0.5 text-xs text-muted">{plural(deHoy.length, "gasto", "gastos")}</p>
          </div>
          <div>
            <p className="rotulo">Este mes</p>
            <p className="tabular mt-1 text-2xl font-semibold text-ink">
              {formatMoney(data.total_gastos, formato)}
            </p>
            <p className="mt-0.5 text-xs text-muted">
              Retirado: {formatMoney(data.total_retiros, formato)}
            </p>
          </div>
        </div>

        {ultimos.length === 0 ? (
          <p className="p-4 text-sm text-muted">No hay gastos registrados este mes.</p>
        ) : (
          <div className="divide-y divide-line">
            {ultimos.map((e) => (
              <FilaDeEgreso key={e.id} egreso={e} formato={formato} monedaPrincipal={data.moneda} />
            ))}
          </div>
        )}
      </div>
    </Tarjeta>
  );
}
